<script setup lang="ts">
import type { Url } from '@global-types/url.ts';
import { computed } from 'vue';
import { useNavbarStore } from '@stores/layout/useNavbarStore';
import Typography from '@components/Typography.vue';
import { storeToRefs } from 'pinia';

interface MenuLinkProps {
  link?: Url,
  hasPin?: boolean,
}

const { link, hasPin = false } = defineProps<MenuLinkProps>();

const navbarStore = useNavbarStore();
const { isCurrentPage } = navbarStore;
const { isPlusMenuOpen } = storeToRefs(navbarStore);

const isActive = computed<boolean>(() => (link ? isCurrentPage(link) && !isPlusMenuOpen.value : false));
</script>

<template>
  <component
    :is="link ? 'a' : 'div'"
    :class="[
      'w-[74px] h-11',
      'flex flex-col space-y-1 justify-center items-center',
      'no-underline'
    ]"
    :href="link"
  >
    <div class="relative flex flex-shrink-0">
      <slot name="icon" :is-active="isActive" />

      <span
        v-if="hasPin"
        class="menu-link__pin"
        :class="[
          'absolute top-0 right-0',
          'rounded-full',
          'w-3 h-3',
        ]"
      />
    </div>

    <Typography
      :class="{ '!font-semibold': isActive }"
      variant="caption"
      :color="isActive ? 'blue' : 'gray-darker'"
    >
      <slot name="text" />
    </Typography>
  </component>
</template>

<style scoped lang="scss">
@use '@styles/variables' as *;

.menu-link {
  &__pin {
    background-color: $colorRed;
    border: 1px solid $colorWhite;
  }
}
</style>
